// frontend/src/components/UserForm.js
// Componente de formulario para crear o editar un usuario.

import React, { useState, useEffect } from 'react';
import { useUserStore } from '../store/userStore';
import { useUIStore } from '../store/uiStore';
import LoadingSpinner from './LoadingSpinner';

/**
 * @component UserForm
 * @description Formulario reutilizable para añadir o editar usuarios.
 * @param {object} props - Propiedades del componente.
 * @param {object|null} [props.userToEdit=null] - Objeto usuario si se está editando.
 * @param {function} props.onSuccess - Callback que se ejecuta al enviar el formulario con éxito.
 * @param {function} props.onCancel - Callback que se ejecuta al cancelar el formulario.
 */
function UserForm({ userToEdit = null, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    displayName: '',
    upn: '',
    role: 'user',
  });
  const { addUser, updateUser } = useUserStore();
  const { isLoading, setIsLoading } = useUIStore();

  const isEditing = !!userToEdit;

  // Cargar datos del usuario a editar
  useEffect(() => {
    if (isEditing) {
      setFormData({
        displayName: userToEdit.displayName || userToEdit.username || '',
        upn: userToEdit.upn || userToEdit.username || '',
        role: userToEdit.role || 'user',
      });
    }
  }, [userToEdit, isEditing]);

  // Manejar cambios en los inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      const dataToSend = {
        displayName: formData.displayName,
        upn: formData.upn,
        role: formData.role,
      };

      if (isEditing) {
        await updateUser(userToEdit.id, dataToSend);
      } else {
        await addUser(dataToSend);
      }
      onSuccess();
    } catch (error) {
      // El store de UI ya muestra el mensaje de error
    } finally {
      setIsLoading(false);
    }
  };

  const formTitle = isEditing ? 'Editar Usuario' : 'Añadir Nuevo Usuario';
  const submitButtonText = isEditing ? 'Guardar Cambios' : 'Crear Usuario';

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto my-8">
      <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">{formTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="displayName"
            name="displayName"
            value={formData.displayName}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="upn" className="block text-sm font-medium text-gray-700">
            Correo <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="upn"
            name="upn"
            value={formData.upn}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
            Rol <span className="text-red-500">*</span>
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="user">Usuario</option>
            <option value="admin">Administrador</option>
          </select>
        </div>

        <div className="pt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="sm" color="text-white" /> : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserForm;
